import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, countryCode, setCountry } = useCountry();
  const { login, user, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // If already authenticated, redirect
  useEffect(() => {
    if (!loading && user) {
      redirectByRole(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  useEffect(() => {
    if (location.state?.reason === 'inactive') {
      setError('Compte désactivé. Contactez votre administrateur.');
    }
  }, [location.state]);

  const redirectByRole = (u) => {
    if (!u) return;
    const role = u.role;
    if (u.must_change_password) {
      navigate('/change-password', { replace: true });
      return;
    }
    if (role === 'superadmin') navigate('/', { replace: true });
    else if (role === 'admin_region' || role === 'admin_commune')
      navigate('/dashboard-admin', { replace: true });
    else if (role === 'agent') navigate('/agent', { replace: true });
    else if (role === 'viewer') navigate('/carte', { replace: true });
    else navigate('/carte', { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error || 'Identifiants incorrects.');
      return;
    }
    if (res.must_change_password) {
      navigate('/change-password', { replace: true });
      return;
    }
    redirectByRole(res.user);
  };

  const gradient =
    country.code === 'quebec'
      ? 'linear-gradient(135deg, #002855 0%, #0057A8 50%, #003DA5 100%)'
      : 'linear-gradient(135deg, #00853F 0%, #006B33 50%, #004D24 100%)';

  return (
    <div
      className="min-h-screen flex items-center justify-center p-5"
      style={{ background: gradient, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-[420px]">
        <div className="flex justify-center gap-2 mb-4">
          <button
            onClick={() => setCountry('quebec')}
            className={`text-xs px-3 py-1 rounded-full transition ${
              countryCode === 'quebec' ? 'bg-white text-gray-900' : 'bg-white/10 text-white border border-white/20'
            }`}
            data-testid="login-country-quebec"
          >
            🍁 Québec
          </button>
          <button
            onClick={() => setCountry('senegal')}
            className={`text-xs px-3 py-1 rounded-full transition ${
              countryCode === 'senegal' ? 'bg-white text-gray-900' : 'bg-white/10 text-white border border-white/20'
            }`}
            data-testid="login-country-senegal"
          >
            🌍 Sénégal
          </button>
        </div>

        <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
          <div className="px-7 py-8 text-center" style={{ background: country.palette.primary }}>
            <div className="text-3xl mb-2">{country.code === 'quebec' ? '🍁' : '🌍'}</div>
            <div className="text-xl font-bold text-white">{country.brand}</div>
            <div className="text-xs mt-1" style={{ color: country.palette.light }}>
              {country.tagline}
            </div>
          </div>

          <form onSubmit={submit} className="px-7 py-7 flex flex-col gap-4">
            {error && (
              <div className="bg-red-50 text-red-700 rounded-lg px-4 py-2.5 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={countryCode === 'quebec' ? 'vous@municipalite.qc.ca' : 'vous@commune.sn'}
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 text-sm outline-none transition"
                onFocus={(e) => (e.target.style.borderColor = country.palette.primaryHover)}
                onBlur={(e) => (e.target.style.borderColor = '#e0e0e0')}
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Mot de passe</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-12 rounded-lg border-2 border-gray-200 text-sm outline-none transition"
                  onFocus={(e) => (e.target.style.borderColor = country.palette.primaryHover)}
                  onBlur={(e) => (e.target.style.borderColor = '#e0e0e0')}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600"
                  tabIndex={-1}
                  data-testid="login-toggle-pwd"
                >
                  {showPwd ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full py-3.5 rounded-xl text-white font-bold text-sm transition disabled:opacity-50"
              style={{ background: country.palette.primary }}
              data-testid="login-submit"
            >
              {busy ? '…' : 'Se connecter'}
            </button>
          </form>

          <div className="px-7 pb-6 text-center text-xs text-gray-500">
            Pas de compte ? Contactez votre administrateur.
            <br />
            <a href="mailto:contact@tmconsultingf.com" className="font-medium" style={{ color: country.palette.primaryHover }}>
              contact@tmconsultingf.com
            </a>
          </div>
        </div>

        <div className="text-center mt-5 text-[11px] text-white/60">
          Propulsé par{' '}
          <a href="mailto:contact@tmconsultingf.com" className="text-white/80">
            TM Consulting & Formation
          </a>
        </div>
      </div>
    </div>
  );
}
